'use client'

import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useCreateTranslation, type CreateTranslationInput } from '@/lib/api/translations'
import { LanguageSelect } from '@/components/language-select'
import { DEFAULT_MESSAGES } from '@/data/messages'


interface CreateTranslationDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    existingLocales: string[]
    storeId: string
    onSuccess: (translationId: string) => void
}

export function CreateTranslationDialog({
    open,
    onOpenChange,
    existingLocales,
    storeId,
    onSuccess,
}: CreateTranslationDialogProps) {
    const { t } = useTranslation('languages')
    const { t: tCommon } = useTranslation('common')
    const { mutateAsync: createTranslation, isPending } = useCreateTranslation()

    const [selectedLocale, setSelectedLocale] = React.useState<string>('')

    const handleCreate = () => {
        if (!selectedLocale) {
            return
        }
        const langCode = selectedLocale.split('-')[0]
        const defaultMessages = DEFAULT_MESSAGES[langCode as keyof typeof DEFAULT_MESSAGES] ?? DEFAULT_MESSAGES.en
        const input: CreateTranslationInput = {
            locale: selectedLocale,
            countryCode: langCode,
            messages: defaultMessages,
            enabled: true,
            storeId,
        }
        createTranslation(input, {
            onSuccess: (result) => {
                onSuccess(result.id)
            },
        })
        setSelectedLocale('')
        onOpenChange(false)
    }

    const handleClose = () => {
        if (isPending) {
            return
        }
        setSelectedLocale('')
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('dialogs.create.title')}</DialogTitle>
                    <DialogDescription>{t('dialogs.create.description')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="locale">{t('dialogs.create.selectLanguage')}</Label>
                        <LanguageSelect
                            value={selectedLocale}
                            onChange={setSelectedLocale}
                            existingLocales={existingLocales}
                            placeholder={t('dialogs.create.placeholder')}
                            emptyMessage={t('dialogs.create.noLocalesAvailable')}
                            searchPlaceholder={t('dialogs.create.searchPlaceholder')}
                            disabled={isPending}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={isPending}>
                        {tCommon('cancel')}
                    </Button>
                    <Button onClick={handleCreate} disabled={!selectedLocale || isPending}>
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('dialogs.create.create')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}